import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { MillingJobType, MillingMaterial } from 'models/milling';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

type AddUpdateMillingJobTypeDialogProps = {
    open: boolean;
    jobType?: MillingJobType;
    onDialogClose: () => void;
    onAdd: (jobType: MillingJobType) => void;
    onUpdate: (jobType: MillingJobType) => void;
    allMaterials: MillingMaterial[];
};

const AddUpdateMillingJobTypeDialog: FC<AddUpdateMillingJobTypeDialogProps> = ({
    open,
    onDialogClose,
    onAdd,
    onUpdate,
    jobType,
    allMaterials,
}: AddUpdateMillingJobTypeDialogProps) => {
    const { t: translate } = useTranslation(['milling-jobs']);
    const [filteredMaterials, setFilteredMaterials] = useState<MillingMaterial[]>([]);
    const [inputValue, setInputValue] = useState('');

    const validate = (values: MillingJobType) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('milling-jobs:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: jobType?.name || '',
            materials: jobType?.materials || [],
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: MillingJobType) => {
            if (jobType?._id) {
                onUpdate({ ...values, _id: jobType?._id });
            } else {
                onAdd({ ...values });
            }
        },
    });

    useEffect(() => {
        filterMaterials();
    }, [allMaterials, formik.values.materials]);

    const handleDialogClose = () => {
        onDialogClose();
    };

    const addMaterial = (material: MillingMaterial) => {
        setInputValue('');
        formik.setFieldValue('materials', [...formik.values.materials, material]);
    };

    const removeMaterial = (index: number) => {
        const materials = [...formik.values.materials];
        materials.splice(index, 1);
        formik.setFieldValue('materials', materials);
    };

    const filterMaterials = () => {
        if (allMaterials) {
            const formMaterials = formik.values.materials as MillingMaterial[];
            const materials = allMaterials.filter(
                (material) => !formMaterials.find((formMaterial) => formMaterial._id === material._id),
            );
            setFilteredMaterials(materials);
        }
    };

    const renderListItem = (material: MillingMaterial, index: number) => (
        <ListItem
            key={material._id}
            secondaryAction={
                <IconButton edge="end" onClick={() => removeMaterial(index)}>
                    <RemoveIcon />
                </IconButton>
            }
        >
            <ListItemText primary={material.name} />
        </ListItem>
    );

    return (
        <Dialog maxWidth="lg" fullWidth open={open}>
            {jobType?._id ? (
                <DialogTitle>{translate('milling-jobs:EDIT_JOB_TYPE')}</DialogTitle>
            ) : (
                <DialogTitle>{translate('milling-jobs:ADD_JOB_TYPE')}</DialogTitle>
            )}
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Box mb={2}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="name"
                                label={translate('common:NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Box>
                    <Typography mb={1} variant="h4">
                        {translate('milling-jobs:MATERIALS')}
                    </Typography>
                    <Box mb={1}>
                        <Paper variant="outlined">
                            <List dense>
                                {formik.values.materials.length > 0 ? (
                                    <Fragment>
                                        {formik.values.materials.map((material, index) =>
                                            renderListItem(material as MillingMaterial, index),
                                        )}
                                    </Fragment>
                                ) : (
                                    <Box p={1}>
                                        <Alert severity="info">{translate('milling-jobs:NO_MATERIALS')}</Alert>
                                    </Box>
                                )}
                            </List>
                        </Paper>
                    </Box>
                    <Box>
                        <Autocomplete
                            options={filteredMaterials}
                            getOptionLabel={(option) => option.name}
                            inputValue={inputValue}
                            disableClearable
                            renderOption={(props, material) => (
                                <MenuItem {...props} key={material._id} value={material.name}>
                                    <ListItemIcon>
                                        <AddIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{material.name}</ListItemText>
                                </MenuItem>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label={translate('milling-jobs:ADD_MATERIAL')} />
                            )}
                            onChange={(_, material) => {
                                if (material) {
                                    addMaterial(material);
                                }
                            }}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'reset') {
                                    setInputValue('');
                                    return;
                                }
                                setInputValue(value);
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                    {jobType?._id ? (
                        <Button
                            disabled={!formik.isValid || formik.values.materials.length === 0}
                            variant="contained"
                            type="submit"
                        >
                            {translate('common:UPDATE')}
                        </Button>
                    ) : (
                        <Button
                            disabled={!formik.isValid || formik.values.materials.length === 0}
                            variant="contained"
                            type="submit"
                        >
                            {translate('common:ADD')}
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddUpdateMillingJobTypeDialog;
